import React, { Fragment, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { DropdownList, Multiselect } from "react-widgets";
import CreatableSelect from 'react-select/creatable';
import { TextInput, DateInput } from "../../../components/form-group";
import ErrorMessage from "../../../components/text/ErrorMessage";
import { toastr } from "react-redux-toastr";
import { uniqBy } from "lodash";

const EditRequirementForm: React.FC<any> = (props) => {
  const {
    onSubmit,
    cities,
    microMarketsData,
    preferredLocationsData,
    projectType,
    propertyType,
    floorPreferences,
    facilities,
    handleCityZones,
    requirement,
    isFormChanged,
    onFormChanged,
    loadingStates: {
      cityLoading,
      cityZoneLoading,
      prefLocLoading,
      propertyTypeLoading,
      projectTypeLoading,
      floorPrefLoading,
      facilityTypeLoading,
    },
    saving,
  } = props;

  const DropdownIndicator = () => {
    return (
      <div></div>
    )
  };

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    errors,
    watch,
    trigger,
    formState: { isDirty },
  } = useForm({
    defaultValues: {
      ...requirement,
      carParking:
        requirement.carParking === true
          ? "yes"
          : requirement.carParking === false
            ? "no"
            : undefined,
      powerBackup:
        requirement.powerBackup === true
          ? "yes"
          : requirement.powerBackup === false
            ? "no"
            : undefined,
    },
  });

  const minArea = watch("minArea");
  const maxArea = watch("maxArea");
  const minPrice = watch("minPrice");
  const maxPrice = watch("maxPrice");
  const minSeats = watch("minSeats");
  const maxSeats = watch("maxSeats");
  const selectedPropertyType =
    propertyType?.find(
      (prptyType: { value: any }) => prptyType.value === watch("propertyType")
    )?.name || "";
  const selectedProjectType =
    projectType?.find(
      (projType: { value: any }) => projType.value === watch("projectType")
    )?.name || "";

  const selectedConstrutionStatus = watch("constructionStatus") || [];

  useEffect(() => {
    trigger("maxPrice");
    trigger("minPrice");
    trigger("lastMoveInDate");
    trigger("earliestMoveInDate");
    trigger("minSeats");
    trigger("maxSeats");
  }, [
    watch("minPrice"),
    watch("earliestMoveInDate"),
    watch("maxPrice"),
    watch("minSeats"),
    watch("maxSeats"),
  ]);

  enum ConstructionStatus {
    Upcoming = "upcoming",
    ReadyToMove = "readyToMove",
    UnderConstruction = "underConstruction",
    Operational = "operational",
    ReadyForFitOuts = "readyForFitOuts",
  }

  enum InteriorType {
    BareShell = "Bare Shell",
    Warmshell = "Warm Shell",
    SemiFurnished = "Semi Furnished",
    PreFurnished = "Pre Furnished",
    FullyFurnished = "Fully Furnished",
  }

  const addSpacesToEnumValue = (value: string): string =>
    value?.replace(/([a-z])([A-Z])/g, "$1 $2");

  const submitHandler = (values: any) => {
    if (!isDirty && requirement?.status === "approved") {
      toastr.error(
        "Error",
        "No changes have been made"
      );
    } else {
      onSubmit({ ...values, preferredLocations: values?.preferredLocations?.map((x: any) => typeof x === 'string' ? x : x.value) });
    }
  }

  useEffect(() => {
    if (isDirty) {
      const matchedValues: any = [];
      microMarketsData.map((obj: any) => {
        if (watch("microMarkets").includes(obj.value)) {
          matchedValues.push(...obj.cityLocations);
        }
      });

      const tempPreferredLocations = [
        ...matchedValues.map((item: { _id: string; cityLocName: string }) => ({
          value: item._id,
          name: item.cityLocName,
        })),
      ];

      setValue("preferredLocations", uniqBy(tempPreferredLocations, "value"));
    }
  }, [watch("microMarkets")]);

  return (
    <form style={{ margin: "0 -15px" }} onSubmit={handleSubmit(submitHandler)}>
      <>
        <div className="col-12 row">
          <div className="col-4 mb-2 pb-1">
            <label className="font-sm font-weight-semibold mb-1" htmlFor="city">
              City:
            </label>
            <Controller
              name="city"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: "City is required",
                },
              }}
              defaultValue={!cityLoading ? requirement.city : null}
              render={({ onChange, value }) => (
                <DropdownList
                  value={!cityLoading ? value : null}
                  data={cities}
                  busy={cityLoading}
                  textField="name"
                  valueField={"value"}
                  filter="contains"
                  onChange={(val) => {
                    onChange(val.value);
                    handleCityZones(val?.value);
                    setValue("microMarkets", []);
                    setValue("preferredLocations", []);
                    onFormChanged();
                  }}
                  placeholder="Select City"
                  disabled={requirement?.status === "approved"}
                />
              )}
            />
            {errors.city && <ErrorMessage>{errors.city.message}</ErrorMessage>}
          </div>

          <div className="col-4 mb-2 pb-1">
            <label
              className="font-sm font-weight-semibold mb-1"
              htmlFor="microMarkets"
            >
              Micro Markets:<span className="text-danger">*</span>
            </label>
            <Controller
              name="microMarkets"
              control={control}
              rules={{
                required: "Micro Markets is required",
                validate: (value) => {
                  return !!(value.length > 0);
                },
              }}
              render={({ onChange, value }) => (
                <Multiselect
                  value={!cityZoneLoading ? value : []}
                  data={microMarketsData}
                  busy={cityZoneLoading}
                  textField={"name"}
                  valueField={"value"}
                  onChange={(val) => {
                    onChange(val?.map((x) => x.value));
                    onFormChanged();
                  }}
                  placeholder="Search"
                  disabled={requirement?.status === "approved"}
                />
              )}
            />
            {errors.microMarkets && (
              <ErrorMessage>Micro Markets is required</ErrorMessage>
            )}
          </div>
          <div className="col-4 mb-2 pb-1">
            <label
              className="font-sm font-weight-semibold mb-1"
              htmlFor="propertyType"
            >
              Property Type:<span className="text-danger">*</span>
            </label>
            <Controller
              name="propertyType"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: "Property Type is required",
                },
              }}
              render={({ onChange, value }) => (
                <DropdownList
                  value={!propertyTypeLoading ? value : null}
                  data={propertyType}
                  busy={propertyTypeLoading}
                  textField={"name"}
                  valueField={"value"}
                  onChange={(val) => {
                    onChange(val.value);
                    onFormChanged();
                  }}
                  placeholder="Select Property Type"
                  disabled={requirement?.status === "approved"}
                />
              )}
            />
            {errors.propertyType && (
              <ErrorMessage>{errors.propertyType.message}</ErrorMessage>
            )}
          </div>
          <div className="col-8 mb-2 pb-1">
            <label
              className="font-sm font-weight-semibold mb-1"
              htmlFor="preferredLocations"
            >
              Preferred Locations:<span className="text-danger">*</span>
            </label>
            <Controller
              name="preferredLocations"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: "Preferred Locations is required",
                },
                validate: (value) => {
                  return !!(value.length > 0);
                },
              }}
              render={({ onChange, value }) => (
                <Multiselect
                  value={!prefLocLoading ? value : []}
                  data={preferredLocationsData}
                  busy={prefLocLoading}
                  textField={"name"}
                  valueField={"value"}
                  onChange={(val) => {
                    onChange(val?.map((x) => x.value));
                    onFormChanged();
                  }}
                  placeholder="Search Locations"
                  disabled={requirement?.status === "approved"}
                />
              )}
            />
            {errors.preferredLocations && (
              <ErrorMessage>Preferred Locations is required</ErrorMessage>
            )}
          </div>

          <div className="col-4 mb-2 pb-1">
            <label
              className="font-sm font-weight-semibold mb-1"
              htmlFor="projectType"
            >
              Project Type:<span className="text-danger">*</span>
            </label>
            <Controller
              name="projectType"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: "Project Type is required",
                },
                validate: (value) => {
                  return !!(value.length > 0);
                },
              }}
              render={({ onChange, value }) => (
                <Multiselect
                  value={!projectTypeLoading ? value : null}
                  data={projectType}
                  busy={projectTypeLoading}
                  textField={"name"}
                  valueField={"value"}
                  onChange={(val) => {
                    onChange(val?.map((x) => x.value));
                    onFormChanged();
                  }}
                  placeholder="Search Property Type"
                  disabled={requirement?.status === "approved"}
                />
              )}
            />
            {errors.projectType && (
              <ErrorMessage>Project Type is required</ErrorMessage>
            )}
          </div>
        </div>
      </>

      <div className="col-12 row">
        <div
          className="col-12 px-3 py-3  mb-3 py-1 font-normal font-weight-semibold bg-gray-100 mt-3 mb-4 "
          style={{ margin: "0 15px" }}
        >
          Additional Property Information
        </div>

        {(selectedPropertyType === "Business Center & Co-working Space" ||
          selectedProjectType === "Business Center & Co-working Space") && (
            <div className="col-4">
              <div className="col-12 px-0  font-normal font-weight-semibold mb-3">
                Select Seats:
              </div>
              <Fragment>
                <div className="row">
                  <div className="col-6 mb-2 pb-1 textInput-component-style">
                    <label
                      className="font-sm font-weight-semibold mb-1"
                      htmlFor="minSeats"
                    >
                      Min Seats:
                      {selectedPropertyType ===
                        "Business Center & Co-working Space" ||
                        selectedProjectType ===
                        "Business Center & Co-working Space" ? (
                        <span className="text-danger">*</span>
                      ) : null}
                    </label>
                    <Controller
                      name="minSeats"
                      control={control}
                      rules={{
                        required: {
                          value:
                            selectedPropertyType ===
                              "Business Center & Co-working Space" ||
                              selectedProjectType ===
                              "Business Center & Co-working Space"
                              ? true
                              : false,
                          message: "Min Seats is required",
                        },
                        validate: (value) => {
                          const numericMaxSeats = maxSeats;
                          if (
                            numericMaxSeats &&
                            (value === undefined ||
                              value === null ||
                              value === "")
                          ) {
                            return "Min Seats is required";
                          }

                          return undefined;
                        },
                      }}
                      render={({ onChange, value, name }) => (
                        <TextInput
                          type="number"
                          placeholder="Min Seats"
                          onChange={onChange}
                          name={name}
                          default={value}
                          gap="no"
                          clearFeildValue={true}
                        />
                      )}
                    />
                    {errors.minSeats && (
                      <ErrorMessage>{errors.minSeats.message}</ErrorMessage>
                    )}
                  </div>

                  <div className="col-6 mb-2 pb-1 textInput-component-style">
                    <label
                      className="font-sm font-weight-semibold mb-1"
                      htmlFor="maxSeats"
                    >
                      Max Seats:
                      {selectedPropertyType ===
                        "Business Center & Co-working Space" ||
                        selectedProjectType ===
                        "Business Center & Co-working Space" ? (
                        <span className="text-danger">*</span>
                      ) : null}
                    </label>

                    <Controller
                      name="maxSeats"
                      control={control}
                      rules={{
                        required: {
                          value:
                            selectedPropertyType ===
                              "Business Center & Co-working Space" ||
                              selectedProjectType ===
                              "Business Center & Co-working Space"
                              ? true
                              : false,
                          message: "Max Seats is required",
                        },
                        validate: (value) => {
                          const numericValue = value;
                          const tempMinSeats = minSeats;
                          return (tempMinSeats || tempMinSeats === 0) &&
                            numericValue !== 0 &&
                            !numericValue
                            ? "Max Seats is required"
                            : (!numericValue && numericValue !== 0) ||
                            (!tempMinSeats && tempMinSeats !== 0) ||
                            (numericValue && numericValue > tempMinSeats) ||
                            (numericValue === 0 && tempMinSeats === 0) ||
                            "Max Seats should be greater than Min Seats";
                        },
                      }}
                      render={({ onChange, value, name }) => (
                        <TextInput
                          type="number"
                          placeholder="Max Seats"
                          onChange={onChange}
                          name={name}
                          default={value}
                          gap="no"
                          clearFeildValue={true}
                        />
                      )}
                    />
                    {errors.maxSeats && (
                      <ErrorMessage>{errors.maxSeats.message}</ErrorMessage>
                    )}
                  </div>
                </div>
              </Fragment>
            </div>
          )}

        <div className="col-4">
          <div className="col-12 font-normal font-weight-semibold mb-3 px-0">
            Select Budget(Per Sq. Ft.)
          </div>
          <div className="row">
            <div className="col-6 mb-2 pb-1 textInput-component-style">
              <label
                className="font-sm font-weight-semibold mb-1"
                htmlFor="minPrice"
              >
                Min Price:
              </label>
              <Controller
                name="minPrice"
                control={control}
                rules={{
                  validate: (value) => {
                    const numericMaxPrice = Number(maxPrice);
                    if (
                      numericMaxPrice &&
                      (value === undefined || value === null || value === "")
                    ) {
                      return "Min Price is required";
                    }
                    return undefined;
                  },
                }}
                render={({ onChange, value, name }) => (
                  <TextInput
                    type="number"
                    placeholder="Min Price"
                    onChange={(val) => {
                      onChange(val);
                      onFormChanged();
                    }}
                    name={name}
                    default={value}
                    gap="no"
                    clearFeildValue={true}
                  />
                )}
              />
              {errors.minPrice && (
                <ErrorMessage>{errors.minPrice.message}</ErrorMessage>
              )}
            </div>

            <div className="col-6 mb-2 pb-1 textInput-component-style">
              <label
                className="font-sm font-weight-semibold mb-1"
                htmlFor="maxPrice"
              >
                Max Price:
              </label>

              <Controller
                name="maxPrice"
                control={control}
                rules={{
                  validate: (value) => {
                    const numericValue = Number(value);
                    const tempMinPrice = Number(minPrice);
                    return tempMinPrice && numericValue !== 0 && !numericValue
                      ? "Max Price is required"
                      : (!numericValue && numericValue !== 0) ||
                      (!tempMinPrice && tempMinPrice !== 0) ||
                      (numericValue && numericValue > tempMinPrice) ||
                      (numericValue === 0 && tempMinPrice === 0) ||
                      "Max Price must be greater than Min Price";
                  },
                }}
                render={({ onChange, value, name }) => (
                  <TextInput
                    type="number"
                    placeholder="Max Price"
                    onChange={(val) => {
                      onChange(val);
                      onFormChanged();
                    }}
                    name={name}
                    default={value}
                    gap="no"
                    clearFeildValue={true}
                  />
                )}
              />
              {errors.maxPrice && (
                <ErrorMessage>{errors.maxPrice.message}</ErrorMessage>
              )}
            </div>
          </div>
        </div>
        <div className="col-4">
          <div className="col-12 font-normal font-weight-semibold mb-3 px-0">
            Area Offered(Sq. Ft.)
          </div>

          <div className="mb-2 pb-1 textInput-component-style">
            <label
              className="font-sm font-weight-semibold mb-1"
              htmlFor="minArea"
            >
              Min Area:<span className="text-danger">*</span>
            </label>
            <Controller
              name="minArea"
              control={control}
              rules={{
                required: "Min Area is required",
                validate: (value) => {
                  const numericMaxArea = Number(maxArea);
                  if (
                    numericMaxArea &&
                    (value === undefined || value === null || value === "")
                  ) {
                    return "Min Area is required";
                  }
                  if (value < 1) {
                    return "Min Area must be greater than 0";
                  }

                  return undefined;
                },
              }}
              render={({ onChange, value, name }) => (
                <TextInput
                  type="number"
                  placeholder="Min Area"
                  onChange={(val) => {
                    onChange(val);
                    trigger("minArea");
                    onFormChanged();
                  }}
                  name={name}
                  default={value}
                  gap="no"
                  clearFeildValue={true}
                />
              )}
            />
            {errors.minArea && (
              <ErrorMessage>{errors.minArea.message}</ErrorMessage>
            )}
          </div>
        </div>

        {(selectedPropertyType === "Business Center & Co-working Space" ||
          selectedProjectType === "Business Center & Co-working Space") && (
            <div className="col-4 mt-3">
              <Fragment>
                <div className=" font-normal font-weight-semibold ">
                  <label
                    className="font-sm font-weight-semibold mb-1"
                    htmlFor="facilityType"
                  >
                    Facility Type:
                    {selectedPropertyType ===
                      "Business Center & Co-working Space" ||
                      selectedProjectType ===
                      "Business Center & Co-working Space" ? (
                      <span className="text-danger">*</span>
                    ) : null}
                  </label>
                </div>
                <div className=" mb-2 pb-1">
                  <Controller
                    name="facilityType"
                    control={control}
                    defaultValue={requirement?.facilityType}
                    rules={{
                      required: {
                        value:
                          selectedPropertyType ===
                            "Business Center & Co-working Space" ||
                            selectedProjectType ===
                            "Business Center & Co-working Space"
                            ? true
                            : false,
                        message: "Facility Type is required",
                      },
                    }}
                    render={({ onChange, value }) => (
                      <DropdownList
                        value={value}
                        data={facilities}
                        busy={facilityTypeLoading}
                        textField={"name"}
                        valueField={"value"}
                        onChange={(selectedValues) => {
                          onChange(selectedValues?.value);
                        }}
                        placeholder="Select Facility Type"
                      />
                    )}
                  />
                  {errors.facilityType && (
                    <ErrorMessage>{errors.facilityType.message}</ErrorMessage>
                  )}
                </div>
              </Fragment>
            </div>
          )}

        <div className="col-4 mt-3">
          {!(
            selectedPropertyType === "Business Center & Co-working Space" ||
            selectedProjectType === "Business Center & Co-working Space"
          ) && <div>&nbsp;</div>}
          <div className=" mb-2 pb-1 textInput-component-style">
            <label
              className="font-sm font-weight-semibold mb-1"
              htmlFor="maxArea"
            >
              Max Area:<span className="text-danger">*</span>
            </label>
            <Controller
              name="maxArea"
              control={control}
              rules={{
                required: "Max Area is required",
                validate: (value) => {
                  const numericValue = Number(value);
                  const tempMinArea = Number(minArea);
                  return tempMinArea && numericValue !== 0 && !numericValue
                    ? "Max Area is required"
                    : (!numericValue && numericValue !== 0) ||
                    (!tempMinArea && tempMinArea !== 0) ||
                    (numericValue && numericValue > tempMinArea) ||
                    (numericValue === 0 && tempMinArea === 0) ||
                    "Max Area must be greater than Min Area";
                },
              }}
              render={({ onChange, value, name }) => (
                <TextInput
                  type="number"
                  placeholder="Max Area"
                  onChange={(val) => {
                    onChange(val);
                    trigger("maxArea");
                    onFormChanged();
                  }}
                  name={name}
                  default={value}
                  gap="no"
                  clearFeildValue={true}
                />
              )}
            />
            {errors.maxArea && (
              <ErrorMessage>{errors.maxArea.message}</ErrorMessage>
            )}
          </div>
        </div>

        <div className="col-4 mt-3">
          <div>
            <div className=" font-sm font-weight-semibold mb-1 ">
              Move-in Date
            </div>
            <div className="row">
              <div className="col-6">
                <>
                  <Controller
                    control={control}
                    name="earliestMoveInDate"
                    render={({ onChange, value }) => (
                      <DateInput
                        default={value}
                        placeholder="Earliest Move in Date"
                        onChange={(val) => {
                          onChange(val);
                          onFormChanged();
                        }}
                      />
                    )}
                  />
                  {errors.earliestMoveInDate && (
                    <ErrorMessage>
                      {errors.earliestMoveInDate.message}
                    </ErrorMessage>
                  )}
                </>
              </div>
              <div className="col-6">
                <>
                  <Controller
                    control={control}
                    rules={{
                      validate: {
                        dateComparison: (value) => {
                          const earliestMoveInDate = watch(
                            "earliestMoveInDate"
                          ); // Get the value of the "toDate" field
                          if (earliestMoveInDate && value) {
                            return (
                              new Date(earliestMoveInDate) < new Date(value) ||
                              "Last Move-in Date should be greater than Earliest Move-in Date"
                            );
                          }
                        },
                      },
                    }}
                    name="lastMoveInDate"
                    render={({ onChange, value }) => (
                      <DateInput
                        default={value}
                        placeholder="Last Move-in Date"
                        onChange={(val) => {
                          onChange(val);
                          onFormChanged();
                        }}
                      />
                    )}
                  />
                  {errors.lastMoveInDate && (
                    <ErrorMessage>{errors.lastMoveInDate.message}</ErrorMessage>
                  )}
                </>
              </div>
            </div>
          </div>
        </div>
        <div className="col-4 mt-3">
          <div className=" font-sm font-weight-semibold mb-1 ">
            Floor Preferences
          </div>
          <Controller
            name="floorPreferences"
            control={control}
            render={({ onChange, value }) => (
              <Multiselect
                value={!floorPrefLoading ? value : []}
                data={floorPreferences}
                busy={floorPrefLoading}
                textField={"name"}
                valueField={"value"}
                onChange={(val) => {
                  onChange(val?.map((x) => x.value));
                  onFormChanged();
                }}
                filter="startsWith"
                messages={
                  floorPreferences?.length === 0
                    ? { emptyList: "No data found" }
                    : {}
                }
                placeholder="Select Floor"
              />
            )}
          />
          {errors.floorPreferences && (
            <ErrorMessage>Floor Preferences is required</ErrorMessage>
          )}
        </div>
        <div className="col-4 mt-3">
          <div className=" font-sm font-weight-semibold mb-1 ">
            Construction Status
          </div>
          <Controller
            name="constructionStatus"
            control={control}
            render={({ onChange, value }) => (
              <Multiselect
                value={value}
                data={Object.entries(ConstructionStatus).map(([name, val]) => ({
                  name: addSpacesToEnumValue(name),
                  value: val,
                }))}
                textField="name"
                valueField="value"
                onChange={(val) => {
                  onChange(val?.map((x) => x.value));
                  const selectedValues = val?.map((x) => x.value) || [];
                  if (
                    !selectedValues?.includes(
                      ConstructionStatus.UnderConstruction
                    )
                  ) {
                    setValue("constructionCompletionDate", "");
                  }
                  onFormChanged();
                }}
                placeholder="Select"
              />
            )}
          />
          {errors.constructionStatus && (
            <ErrorMessage>Construction Status is required</ErrorMessage>
          )}
        </div>

        {selectedConstrutionStatus &&
          selectedConstrutionStatus?.some(
            (item: string) => item === ConstructionStatus.UnderConstruction
          ) && (
            <>
              <div className="col-4 mt-3">
                <div className=" font-sm font-weight-semibold mb-1 ">
                  Construction Completion Date
                </div>
                <div className="col-3 mb-2 pb-1">
                  <Controller
                    control={control}
                    name="constructionCompletionDate"
                    render={({ onChange, value }) => (
                      <DateInput
                        default={value}
                        placeholder="Date"
                        onChange={(val) => {
                          onChange(val);
                        }}
                        minDate={new Date()}
                      />
                    )}
                  />
                </div>
              </div>
            </>
          )}

        <div className="col-4 mt-3">
          <div className=" font-sm font-weight-semibold mb-1 ">
            Interior Type
          </div>
          <Controller
            name="interiorType"
            control={control}
            defaultValue={requirement?.interiorType}
            render={({ onChange, value }) => (
              <Multiselect
                value={value}
                data={Object.entries(InteriorType).map(([name, val]) => ({
                  name: addSpacesToEnumValue(name),
                  value: val,
                }))}
                textField="name"
                valueField="value"
                placeholder="Select"
                onChange={(val) => {
                  onChange(val);
                }}
              />
            )}
          />
        </div>
        <div className="col-4 mt-4">
          {!(
            selectedPropertyType === "Business Center & Co-working Space" ||
            selectedProjectType === "Business Center & Co-working Space"
          ) && (
              <div className=" font-sm font-weight-semibold mb-1 ">&nbsp;</div>
            )}
          <span className="mr-4">
            <Controller
              control={control}
              name="includeGradeC"
              render={({ onChange, value, name }) => (
                <Fragment>
                  <input
                    type="checkbox"
                    id={name}
                    checked={value}
                    onChange={(e) => onChange(e.target.checked)}
                  />
                  <label htmlFor={name}>Include Grade C</label>
                </Fragment>
              )}
            />
          </span>
          <Controller
            control={control}
            name="includeLeaseExpiry"
            render={({ onChange, value, name }) => (
              <Fragment>
                <input
                  type="checkbox"
                  id={name}
                  checked={value}
                  onChange={(e) => onChange(e.target.checked)}
                />
                <label htmlFor={name}>Include Lease Expiry Options</label>
              </Fragment>
            )}
          />
        </div>

        <div
          className="col-12 px-3 py-3   py-1 font-normal font-weight-semibold bg-gray-100 mt-3  "
          style={{ margin: "0 15px" }}
        >
          Proposal Details
        </div>

        <div className="col-12">
          <div className="row py-3">
            <div className="col-3">
              <div className=" font-sm font-weight-semibold mb-1 ">
                Proposal Due By
              </div>
              <div className="row">
                <div className="col-12 mb-2 pb-1">
                  <>
                    <Controller
                      control={control}
                      name="proposalDueBy"
                      render={({ onChange, value }) => (
                        <DateInput
                          default={value}
                          placeholder="Choose Date"
                          onChange={onChange}
                          minDate={new Date()}
                        />
                      )}
                    />
                    {errors.proposaldueby && (
                      <ErrorMessage>
                        {errors.proposaldueby.message}
                      </ErrorMessage>
                    )}
                  </>
                </div>
              </div>
            </div>

          </div>
        </div>

        <div
          className="col-12 px-3 py-3   py-1 font-normal font-weight-semibold bg-gray-100 mb-3 "
          style={{ margin: "0 15px" }}
        >
          Forecast & Certainity
        </div>
        <div className="col-2 mb-2 pb-1 textInput-component-style">
          <label
            className="font-sm font-weight-semibold mb-1"
            htmlFor="forecast"
          >
            Forecast (Amount):
          </label>
          <Controller
            name="forecast"
            control={control}
            defaultValue={requirement?.forecast}
            render={({ onChange, value, name }) => (
              <TextInput
                type="number"
                placeholder="Forecast"
                onChange={onChange}
                name={name}
                default={value}
                gap="no"
                clearFeildValue={true}
                step="any"
                maxValue={"100000000"}
              />
            )}
          />
        </div>

        <div className="col-2 mb-2 pb-1 textInput-component-style">
          <label
            className="font-sm font-weight-semibold mb-1"
            htmlFor="certainity"
          >
            Certainity (%):
          </label>
          <Controller
            name="certainity"
            control={control}
            defaultValue={requirement?.certainity}
            render={({ onChange, value, name }) => (
              <TextInput
                type="number"
                placeholder="Certainity"
                onChange={onChange}
                name={name}
                default={value}
                gap="no"
                clearFeildValue={true}
                step="any"
                maxValue={"100"}
              />
            )}
          />
        </div>

        <div className="col-12 font-normal font-weight-semibold pt-3 mb-2 ">
          Special Requirements
        </div>
        <div className="col-6 mb-2 pb-1">
          <Controller
            name="specialRequirements"
            control={control}
            defaultValue={requirement?.specialRequirements}
            render={({ onChange, value }) => (
              <textarea
                rows={4}
                className="form-control rounded-sm font-weight-semibold"
                value={value}
                onChange={onChange}
                placeholder={"Enter Special Requirements"}
              />
            )}
          />
        </div>
        <div className="col-12 font-normal font-weight-semibold pt-3 mb-2">
          <div className="row">
            <div className="col-6 mb-2 pb-1">
              <label
                className="font-normal font-weight-semibold mb-1"

              >
                Core Requirement
              </label>

              <Controller
                name="coreRequirements"
                defaultValue={requirement?.coreRequirements}
                control={control}
                render={({ onChange, value }) => {
                  return (
                    <CreatableSelect
                      components={{ DropdownIndicator, IndicatorSeparator: () => null }}
                      isClearable
                      isMulti
                      openMenuOnClick={false}
                      value={value?.map((val: any) => ({ value: val, label: val }))}
                      onChange={(data) => {
                        onChange(data?.map((details: any) => (details.value)))
                      }}
                      placeholder="Enter core requirements"
                      className="custom-creatable-select"
                    />
                  )
                }}
              />
            </div>
            <div className="col-6 mb-2 pb-1">
              <label
                className="font-normal font-weight-semibold mb-1"

              >
                Requirement Parameters
              </label>

              <Controller
                name="coreRequirementParameters"
                defaultValue={requirement?.coreRequirementParameters}
                control={control}
                render={({ onChange, value }) => (
                  <CreatableSelect
                    components={{ DropdownIndicator, IndicatorSeparator: () => null }}
                    isClearable
                    isMulti
                    openMenuOnClick={false}
                    value={value?.map((val: any) => ({ value: val, label: val }))}
                    onChange={(details) => {
                      onChange(details?.map((data: any) => (data.value)))
                    }}
                    placeholder="Enter requirement parameters"
                    className="custom-creatable-select"
                  />
                )}
              />
            </div>
          </div>
        </div>

        <div className="col-12 font-normal font-weight-semibold pt-3 mb-2 ">
          <button
            type="button"
            onClick={() => {
              reset();
              props.onClose?.();
            }}
            className="btn btn-outline-secondary font-base font-weight-bold mr-2 px-4 rounded"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary font-base font-weight-bold px-5 rounded"
            disabled={saving}
          >
            Save
          </button>
          <br />
          {!isFormChanged && <ErrorMessage>No changes were found</ErrorMessage>}
        </div>
      </div>
    </form>
  );
};
export default EditRequirementForm;
